import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { Button, Card, CardActions, CircularProgress } from "@mui/material";
import {
  Form,
  required,
  TextInput,
  useLogin,
  useNotify,
  useTranslate,
} from "react-admin";

import Box from "@mui/material/Box";
import { consumeAuthRedirectMessage } from "../authProvider";

const RECAPTCHA_ENTERPRISE_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY ||
  "6LcwHVwtAAAAAGds-UOYIJxoueTYhRzalHUyDBVR";
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-enterprise";

let recaptchaScriptPromise: Promise<void> | null = null;

function loadRecaptchaEnterpriseScript() {
  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.getElementById(
        RECAPTCHA_SCRIPT_ID
      ) as HTMLScriptElement | null;

      const handleLoad = () => resolve();
      const handleError = () => {
        recaptchaScriptPromise = null;
        reject(new Error("Failed to load reCAPTCHA Enterprise"));
      };

      if (existingScript) {
        existingScript.addEventListener("load", handleLoad, { once: true });
        existingScript.addEventListener("error", handleError, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_ENTERPRISE_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });
      document.head.appendChild(script);
    });
  }

  return recaptchaScriptPromise.then(
    () =>
      new Promise<void>((resolve, reject) => {
        if (!window.grecaptcha?.enterprise) {
          reject(new Error("reCAPTCHA Enterprise is unavailable"));
          return;
        }

        window.grecaptcha.enterprise.ready(() => resolve());
      })
  );
}

async function getRecaptchaToken() {
  try {
    await loadRecaptchaEnterpriseScript();

    if (!window.grecaptcha?.enterprise) {
      return undefined;
    }

    return await window.grecaptcha.enterprise.execute(
      RECAPTCHA_ENTERPRISE_SITE_KEY,
      {
        action: "LOGIN",
      }
    );
  } catch {
    return undefined;
  }
}

const Login = () => {
  const [loading, setLoading] = useState(false);
  const translate = useTranslate();

  const notify = useNotify();
  const login = useLogin();
  const location = useLocation();

  useEffect(() => {
    const message = consumeAuthRedirectMessage();

    if (!message) {
      return;
    }

    notify(message, {
      type: "warning",
      autoHideDuration: 4000,
      anchorOrigin: { vertical: "top", horizontal: "center" },
    });
  }, [notify]);

  useEffect(() => {
    void loadRecaptchaEnterpriseScript().catch(() => undefined);
  }, []);

  const handleSubmit = async (auth: FormValues) => {
    setLoading(true);

    try {
      //如果当前网站时localhost环境，则不需要进行recaptcha验证，直接登录
      if (window.location.hostname === "localhost") {
        const recaptchaToken = "";
        await login(
          { ...auth, recaptchaToken },
          location.state ? (location.state as any).nextPathname : "/"
        );
      } else {
        const recaptchaToken = await getRecaptchaToken();

        if (!recaptchaToken) {
          notify("reCAPTCHA failed to load. Continuing without verification.", {
            type: "warning",
          });
        }
        await login(
          { ...auth, recaptchaToken },
          location.state ? (location.state as any).nextPathname : "/"
        );
      }
    } catch (error) {
      setLoading(false);

      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : undefined;

      notify(errorMessage ?? "ra.auth.sign_in_error", {
        type: "error",
        messageArgs: {
          _: errorMessage,
        },
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "flex-start",
          backgroundColor: "#f2f7fa",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <Card sx={{ minWidth: 300, marginTop: "6em", padding: "2em" }}>
          <Box
            sx={{
              margin: "1em",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img src="/images/happymango_logo.png" alt="logo" />
          </Box>
          <Box
            sx={{
              marginTop: "1em",
              display: "flex",
              justifyContent: "center",
              color: (theme) => theme.palette.grey[500],
            }}
          >
            LOG IN TO HAPPY MANGO INSITUTIONAL ACCOUNT
          </Box>
          <Box
            sx={{
              marginTop: "1em",
              display: "flex",
              justifyContent: "center",
              color: (theme) => theme.palette.grey[500],
            }}
          >
            This website is for institutional users only. Click here to log into
            your individual user account.
          </Box>
          <Box sx={{ padding: "0 1em 1em 1em" }}>
            <Box sx={{ marginTop: "1em" }}>
              <TextInput
                autoFocus
                variant="outlined"
                source="email"
                label="Email"
                disabled={loading}
                validate={required()}
                fullWidth
                autoComplete="off"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <Box sx={{ marginTop: "1em" }}>
              <TextInput
                variant="outlined"
                source="password"
                label="Password"
                type="password"
                disabled={loading}
                validate={required()}
                fullWidth
                autoComplete="off"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Box>
          <CardActions
            sx={{
              padding: "0 1em 1em 1em",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              fullWidth
              sx={{
                backgroundColor: "#00ccb1",
                "&:hover": {
                  backgroundColor: "#00b39b",
                },
              }}
            >
              {loading && <CircularProgress size={25} thickness={2} />}
              {translate("ra.auth.sign_in")}
            </Button>
          </CardActions>
          <Box
            sx={{
              textAlign: "right",
              width: "100%",
              mt: 1,
            }}
          >
            <Link
              to="/forget-password"
              style={{
                textDecoration: "none",
                color: "#00CCB1",
              }}
            >
              Forget Password?
            </Link>
          </Box>
        </Card>
      </Box>
    </Form>
  );
};

Login.propTypes = {
  authProvider: PropTypes.func,
  previousRoute: PropTypes.string,
};

export default Login;

interface FormValues {
  username?: string;
  email?: string;
  password?: string;
  recaptchaToken?: string;
}
